'use client';

import { useState } from 'react';
import { Search } from 'lucide-react';
import { cn } from '../utils/cn';
import type { SearchLineModalMobileProps } from './types';

/**
 * Модальное окно поиска для мобильной версии
 * Открывается сверху без заголовка, только с крестиком закрытия
 */
export function SearchLineModalMobile({
  translations = {},
  TriggerComponent,
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  children,
  triggerClassName = '',
}: SearchLineModalMobileProps) {
  const [isOpen, setIsOpen] = useState(false);

  const searchText = translations.search || 'Поиск';

  // Если Dialog не передан, выводим предупреждение, но не падаем
  if (!Dialog || !DialogContent) {
    console.warn(
      'SearchLineModalMobile: Dialog and DialogContent components must be provided. ' +
        'Please pass them as props from your UI library (e.g., @/components/ui/dialog).'
    );
    return null;
  }

  const handleOpen = () => setIsOpen(true);

  // Рендерим триггер
  const triggerContent = (
    <>
      <Search className="text-gray-500 w-5" size={20} />
      <span className="text-sm">{searchText}</span>
    </>
  );

  const triggerElement = TriggerComponent ? (
    <TriggerComponent onClick={handleOpen}>{triggerContent}</TriggerComponent>
  ) : Button ? (
    <Button
      size="sm"
      className={cn('bg-gray-50 md:hidden rounded-full', triggerClassName)}
      onClick={handleOpen}
      variant="outline"
    >
      {triggerContent}
    </Button>
  ) : (
    <button
      type="button"
      className={cn(
        'bg-gray-50 md:hidden rounded-full',
        'inline-flex items-center justify-center gap-2',
        'px-4 py-2 border border-gray-200 hover:bg-gray-100',
        triggerClassName
      )}
      onClick={handleOpen}
    >
      {triggerContent}
    </button>
  );

  return (
    <>
      {triggerElement}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent
          className={cn(
            // Переопределяем позиционирование: открывается сверху
            '!fixed !left-[50%] !top-4 !translate-x-[-50%] !translate-y-0',
            // Размеры
            '!w-full !max-w-[calc(100%-2rem)] sm:!max-w-[425px]',
            // Отступы с учетом кнопки закрытия сверху
            'gap-4 p-4 pt-12',
            // Анимации для открытия сверху
            'data-[state=closed]:!slide-out-to-top-[-10%]',
            'data-[state=open]:!slide-in-from-top-[-10%]'
          )}
        >
          {/* Скрытый заголовок для доступности screen readers */}
          {DialogTitle && (
            <DialogTitle className="sr-only">
              {searchText}
            </DialogTitle>
          )}
          {children}
        </DialogContent>
      </Dialog>
    </>
  );
}
